import { useCallback, useEffect, useState } from 'react';
import { ReactComponent as FlowIcon } from '../../../assets/Icons/adornments/DataFlow.svg';
import { IndividualAirflowJob } from '../pages/IndividualJob';
import CustomFlowNode from './CustomFlowNode';
import Dagre from '@dagrejs/dagre';

import { 
    ReactFlow, 
    Controls, 
    Background, 
    Node, 
    Edge,
    Position,
    applyNodeChanges,
    NodeChange,
    EdgeChange,
    applyEdgeChanges,
    useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

const nodeTypes = {
    statusNode: CustomFlowNode,
}

export default function FlowDiagram({
    job,
    tasks,
}: {
    job: IndividualAirflowJob | null,
    tasks: any[],
}) {
    const [ nodes, setNodes ] = useState<Node[]>([]);
    const [ edges, setEdges ] = useState<Edge[]>([]);

    const { fitView } = useReactFlow();

    useEffect(() => {
        if (job && tasks.length > 0) {
            let taskNodes: Node[] = [];
            let taskEdges: Edge[] = [];

            const taskIds = Array.from(new Set(tasks.map(task => task.task_instance.task_id)));

            const finalReport = job.dag_final_state_report;
            if (finalReport && taskIds.length > 0) {
                const taskInfo = finalReport.report.task_info;

                taskIds.forEach((taskId,i) => {
                    const matchingTaskKey = Object.keys(taskInfo).find(info => info.includes(taskId));

                    if (matchingTaskKey) {
                        const taskInfoVal = taskInfo[matchingTaskKey];

                        taskNodes.push({
                            id: taskId,
                            type: 'statusNode',
                            position: { x: i*100, y: 0 },
                            data: { label: taskId, status: taskInfoVal.state },
                            sourcePosition: Position.Right,
                            targetPosition: Position.Left,
                        })
                    };
                });

                if (taskNodes.length > 0) {
                    taskNodes.forEach(taskNode => {
                        let downstreamTasks: string[] = [];
    
                        Object.keys(taskInfo).forEach(info => {
                            const infoVal = taskInfo[info];

                            if (infoVal.upstream_tasks.length > 0) {
                                const upstreamTasks: string[] = [];
                                
                                infoVal.upstream_tasks.forEach(task => {
                                    const splitTask = task.split('__');
                                    if (splitTask.length > 1) {
                                        splitTask.pop();
                                    };

                                    upstreamTasks.push(splitTask.join(''));

                                });

                                if (upstreamTasks.includes(taskNode.id)) {
                                    const matchingId = taskIds.find(id => info.includes(id));
                                    if (matchingId) {
                                        downstreamTasks.push(matchingId);
                                    }
                                };
                                
                            };
                        });

                        const allDownstreamTasks = Array.from(new Set(downstreamTasks));

                        allDownstreamTasks.forEach(downstreamTask => {
                            taskEdges.push({
                                id: `${taskNode.id}-${downstreamTask}`,
                                source: taskNode.id,
                                target: downstreamTask
                            })
                        });
                    })
                };
            };

            setNodes(taskNodes);
            setEdges(taskEdges);

            
        }
    }, [job, tasks]);

    const layoutNodes = (nodes: Node[], edges: Edge[], direction: string = 'LR') => {
        const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
        g.setGraph({ rankdir: direction });

        edges.forEach((edge) => g.setEdge(edge.source, edge.target));
        nodes.forEach((node) => g.setNode(node.id, {
            ...node,
            width: node.measured?.width ?? 0,
            height: node.measured?.height ?? 0,
        }));

        Dagre.layout(g);

        return {
            nodes: nodes.map((node) => {
                const position = g.node(node.id);
                const x = position.x - (node.measured?.width ?? 0) / 2;
                const y = position.y - (node.measured?.height ?? 0) / 2;

                return {...node, position: { x,y }};
            }),
            edges,
        }
    };

    useEffect(() => {
        if (nodes.length > 0 && edges.length > 0) {
            const layouted = layoutNodes(nodes, edges, 'LR');

            setNodes([...layouted.nodes]);
            setEdges([...layouted.edges]);

            setTimeout(() => fitView({ padding: 0.6 }), 0);

        }
    }, [nodes, edges, fitView]);

    const onNodesChange = useCallback(
        (changes: NodeChange[]) => setNodes((nds) => applyNodeChanges(changes, nds)),
        [setNodes]
    );

    const onEdgesChange = useCallback(
        (changes: EdgeChange[]) => setEdges((eds) => applyEdgeChanges(changes, eds)),
        [setEdges]
    );

    



    return (
        <>
        {job && tasks.length > 0 ? (
            <div className="mt-10 w-full flex flex-col justify-start items-stretch gap-2">
                <div className="flex justify-between items-center gap-4">
                    <div className="flex justify-start items-center gap-1">
                        <div className="w-[20px] text-default"><FlowIcon /></div>
                        <p className="text-[20px] font-semibold text-default">Task Flow Chart</p>
                    </div>
                </div>

                <div className='w-full h-[600px] bg-white rounded-lg'>
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        nodeTypes={nodeTypes}
                        fitView
                        nodesConnectable={false}
                    >
                        <Background />
                        <Controls />
                    </ReactFlow>
                </div>
            </div>
        ) : (
            <></>
        )}
        </>
    )
}